// Le chiamate API usano SEMPRE un percorso relativo "/api": così la web app
// funziona su qualunque dominio (incluso tavoline.it) senza cambiare env né ri-deployare.
export const BACKEND_URL = "";
const BASE = "/api";
export const TOKEN_KEY = "tavoline.access_token";

// Prefix relative /api/... media paths (uploaded photos). Con BACKEND_URL vuoto
// restano relative all'origine corrente; gli URL assoluti (es. pexels) passano invariati.
export function mediaUrl(path) {
  if (!path) return undefined;
  if (path.startsWith("http")) return path;
  return path;
}

async function request(path, opts = {}) {
  const headers = { "Content-Type": "application/json" };
  if (opts.auth) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method || "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail = (data && data.detail) || "Errore di rete";
    throw new Error(typeof detail === "string" ? detail : "Errore");
  }
  return data;
}

export const api = {
  // public
  listTenants: () => request("/public/tenants"),
  getTenant: (slug) => request(`/public/${slug}`),
  getMenu: (slug) => request(`/public/${slug}/menu`),
  getPrivacy: (slug) => request(`/public/${slug}/privacy`),
  register: (body) => request("/public/register", { method: "POST", body }),
  verifyEmail: (token) => request("/public/verify", { method: "POST", body: { token } }),
  requestPro: (nota) => request("/admin/request-pro", { method: "POST", auth: true, body: { nota } }),
  deleteAccount: (password) => request("/admin/account/delete", { method: "POST", auth: true, body: { password } }),
  saSetPlan: (id, piano, nota = "") => request(`/superadmin/tenants/${id}/plan`, { method: "POST", auth: true, body: { piano, nota } }),
  saSetPlanFull: (id, body) => request(`/superadmin/tenants/${id}/plan`, { method: "POST", auth: true, body }),
  saBlock: (id, bloccato, nota = "") => request(`/superadmin/tenants/${id}/block`, { method: "POST", auth: true, body: { bloccato, nota } }),
  checkTable: (slug, label) => request(`/public/${slug}/table/check`, { method: "POST", body: { label } }),
  openSession: (slug, label, guest_name) => request(`/public/${slug}/session/open`, { method: "POST", body: { label, guest_name } }),
  joinSession: (slug, label, code, guest_name) => request(`/public/${slug}/session/join`, { method: "POST", body: { label, code, guest_name } }),
  getSession: (sessionId) => request(`/public/session/${sessionId}`),
  setCoperti: (sessionId, coperti) => request(`/public/session/${sessionId}/coperti`, { method: "POST", body: { coperti } }),
  createOrder: (body) => request(`/public/orders`, { method: "POST", body }),
  demoKds: (sessionId) => request(`/public/demo/kds/${sessionId}`),
  // auth
  login: (email, password) => request("/auth/login", { method: "POST", body: { email, password } }),
  me: () => request("/auth/me", { auth: true }),
  // staff/kds
  kdsTables: () => request("/kds/tables", { auth: true }),
  setOrderStatus: (orderId, stato, motivo) => request(`/kds/orders/${orderId}/status`, { method: "PATCH", auth: true, body: { stato, motivo } }),
  paySession: (sessionId) => request(`/kds/session/${sessionId}/pay`, { method: "POST", auth: true }),
  reassign: (sessionId, label) => request(`/kds/session/${sessionId}/reassign`, { method: "POST", auth: true, body: { label } }),
  // admin
  getSettings: () => request("/admin/settings", { auth: true }),
  putSettings: (body) => request("/admin/settings", { method: "PUT", auth: true, body }),
  adminTables: () => request("/admin/tables", { auth: true }),
  createTable: (etichetta) => request("/admin/tables", { method: "POST", auth: true, body: { etichetta } }),
  bulkTables: (da, a, prefix = "") => request("/admin/tables/bulk", { method: "POST", auth: true, body: { da, a, prefix } }),
  updateTable: (id, body) => request(`/admin/tables/${id}`, { method: "PATCH", auth: true, body }),
  closeTableSession: (id) => request(`/admin/tables/${id}/close-session`, { method: "POST", auth: true }),
  adminMenuFull: () => request("/admin/menu/full", { auth: true }),
  createCategory: (body) => request("/admin/categories", { method: "POST", auth: true, body }),
  updateCategory: (id, body) => request(`/admin/categories/${id}`, { method: "PATCH", auth: true, body }),
  deleteCategory: (id) => request(`/admin/categories/${id}`, { method: "DELETE", auth: true }),
  reorderCategories: (ids) => request("/admin/categories/reorder", { method: "POST", auth: true, body: { ordered_ids: ids } }),
  azzeraOggi: (id) => request(`/admin/categories/${id}/azzera-oggi`, { method: "POST", auth: true }),
  createItem: (body) => request("/admin/items", { method: "POST", auth: true, body }),
  updateItem: (id, body) => request(`/admin/items/${id}`, { method: "PATCH", auth: true, body }),
  deleteItem: (id) => request(`/admin/items/${id}`, { method: "DELETE", auth: true }),
  setAvailability: (id, disponibile) => request(`/admin/items/${id}/availability`, { method: "PATCH", auth: true, body: { disponibile } }),
  uploadPhoto: async (file) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${BASE}/admin/upload-photo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || ""}` },
      body: form,
    });
    if (!res.ok) {
      let msg = "Upload fallito";
      try { msg = (await res.json()).detail || msg; } catch {}
      throw new Error(msg);
    }
    return res.json();
  },
  qrPdfUrl: (copies) => {
    const token = localStorage.getItem(TOKEN_KEY) || "";
    return `${BASE}/admin/qr-pdf?token=${encodeURIComponent(token)}&copies=${copies}&base_url=${encodeURIComponent(window.location.origin)}`;
  },
  // superadmin
  saTenants: () => request("/superadmin/tenants", { auth: true }),
  saCreateTenant: (body) => request("/superadmin/tenants", { method: "POST", auth: true, body }),
  saUpdateTenant: (id, body) => request(`/superadmin/tenants/${id}`, { method: "PATCH", auth: true, body }),
  saStorage: () => request("/superadmin/storage", { auth: true }),
  saPurgeOrders: (months = 24) => request(`/superadmin/maintenance/purge-orders?months=${months}`, { method: "POST", auth: true }),
  saInactiveFree: () => request("/superadmin/maintenance/inactive-free", { method: "POST", auth: true }),
};
